import { useCallback, useState } from 'react';
import { Mixpanel } from '../../../../../../../utils/mixpanel';
import {
    AICA_ASK_AICA_SUGGESTIONS_TOGGLE_CLICKED,
    AICA_ASK_AICA_SUGGESTION_CLICKED,
} from '../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { AgentInput } from '../../../../../../../store/dashboard/askAica/actions';
import { SUGGESTIONS_STRIP_SOURCE } from '../../../../../../../store/dashboard/askAica/constants';

interface UseSuggestionsStripArgs {
    /** Whether this surface shows suggestions at all (mobile: !isViewOnly; LeftPanel: showSuggestionsToggle). */
    enabled: boolean;
    isChatEmpty: boolean;
    isLoading: boolean;
    /** Distinguishes one chat from the next (e.g. selectedConversationId) — a stale
     * key falls back to isChatEmpty, resetting the strip on New Chat / conversation switch. */
    resetKey?: string | null;
    /** Memoize this (e.g. useMemo on its fields) — an unstable reference recreates
     * toggleSuggestions/handleSuggestionTap on every render. */
    trackingProps: { pnoId?: string; userId?: string; organizationId?: string };
    onSend: (text: string, sources?: string[], flowKey?: string, agentInput?: AgentInput) => (() => void) | undefined;
}

interface SuggestionsOverride {
    key?: string | null;
    open: boolean;
}

export function useSuggestionsStrip({ enabled, isChatEmpty, isLoading, resetKey, trackingProps, onSend }: UseSuggestionsStripArgs) {
    const [override, setOverride] = useState<SuggestionsOverride | null>(null);
    const manualOpen = override && override.key === resetKey ? override.open : null;
    const suggestionsVisible = enabled && (manualOpen ?? isChatEmpty);

    const toggleSuggestions = useCallback(() => {
        Mixpanel.track(AICA_ASK_AICA_SUGGESTIONS_TOGGLE_CLICKED, { ...trackingProps, opened: !suggestionsVisible });
        setOverride({ key: resetKey, open: !suggestionsVisible });
    }, [trackingProps, suggestionsVisible, resetKey]);

    const closeSuggestions = useCallback(() => setOverride({ key: resetKey, open: false }), [resetKey]);
    const resetSuggestions = useCallback(() => setOverride(null), []);

    const handleSuggestionTap = useCallback(
        (text: string, flowKey?: string, agentInput?: AgentInput) => {
            if (isLoading) return;
            Mixpanel.track(AICA_ASK_AICA_SUGGESTION_CLICKED, { suggestion: text, ...trackingProps, source: SUGGESTIONS_STRIP_SOURCE });
            onSend(text, undefined, flowKey, agentInput);
            closeSuggestions();
        },
        [isLoading, trackingProps, onSend, closeSuggestions],
    );

    // Closes the strip on every composer send (typed messages) — the same close-on-send
    // handleSuggestionTap does above for a tapped pill. The one wrapper both callers need.
    const handleComposerSend = useCallback(
        (text: string, sources?: string[], flowKey?: string, agentInput?: AgentInput) => {
            closeSuggestions();
            return onSend(text, sources, flowKey, agentInput);
        },
        [closeSuggestions, onSend],
    );

    return { suggestionsVisible, toggleSuggestions, handleSuggestionTap, handleComposerSend, closeSuggestions, resetSuggestions };
}
